import {
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Space,
  Card,
  Typography,
} from 'antd';
import { ScanOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCreateReceipt, useLocations } from '@hooks/useWarehouse';
import ProductSearchSelect from '@components/ProductSearchSelect';
import type { ReceiptItem } from '@/types/warehouse';

const { TextArea } = Input;

interface ReceiptFormValues {
  productId: string;
  udiScanned?: string;
  batchNumber?: string;
  serialNumber?: string;
  expiryDate?: dayjs.Dayjs;
  quantity: number;
  locationCode: string;
}

export default function ReceiptForm() {
  const [mdForm] = Form.useForm<ReceiptFormValues>();
  const mdCreateReceipt = useCreateReceipt();
  const { data: mdLocations } = useLocations();

  const mdLocationOptions = (mdLocations ?? [])
    .filter((mdLoc) => mdLoc.isActive)
    .map((mdLoc) => ({
      value: mdLoc.code,
      label: mdLoc.code + (mdLoc.zone ? ` (${mdLoc.zone})` : ''),
    }));

  const handleParseUdi = () => {
    const mdUdi = mdForm.getFieldValue('udiScanned') as string | undefined;
    if (!mdUdi) return;

    // Client-side GS1 AI parsing (basic)
    const mdBatchMatch = mdUdi.match(/10([^\x1d]{1,20})/);
    const mdSerialMatch = mdUdi.match(/21([^\x1d]{1,20})/);
    const mdExpiryMatch = mdUdi.match(/17(\d{6})/);

    if (mdBatchMatch) {
      mdForm.setFieldValue('batchNumber', mdBatchMatch[1]);
    }
    if (mdSerialMatch) {
      mdForm.setFieldValue('serialNumber', mdSerialMatch[1]);
    }
    if (mdExpiryMatch) {
      const mdRaw = mdExpiryMatch[1];
      const mdYear = 2000 + parseInt(mdRaw.substring(0, 2), 10);
      const mdMonth = parseInt(mdRaw.substring(2, 4), 10);
      const mdDay = parseInt(mdRaw.substring(4, 6), 10) || 1;
      mdForm.setFieldValue('expiryDate', dayjs(new Date(mdYear, mdMonth - 1, mdDay)));
    }
  };

  const handleSubmit = async (mdValues: ReceiptFormValues) => {
    const mdItem: ReceiptItem = {
      productId: mdValues.productId,
      udiScanned: mdValues.udiScanned,
      batchNumber: mdValues.batchNumber,
      serialNumber: mdValues.serialNumber,
      expiryDate: mdValues.expiryDate ? mdValues.expiryDate.format('YYYY-MM-DD') : undefined,
      quantity: mdValues.quantity,
      locationCode: mdValues.locationCode,
    };

    await mdCreateReceipt.mutateAsync([mdItem]);
    mdForm.resetFields();
  };

  return (
    <Card style={{ maxWidth: 700 }}>
      <Typography.Title level={5} style={{ marginBottom: 24 }}>
        Оприбуткування товару
      </Typography.Title>

      <Form
        form={mdForm}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Form.Item
          label="Продукт"
          name="productId"
          rules={[{ required: true, message: 'Оберіть продукт' }]}
        >
          <ProductSearchSelect placeholder="Пошук продукту..." />
        </Form.Item>

        <Form.Item label="UDI сканування" name="udiScanned">
          <Space.Compact style={{ width: '100%' }}>
            <TextArea
              rows={1}
              placeholder="Скануйте або введіть UDI/GS1 код..."
              style={{ flex: 1 }}
            />
            <Button icon={<ScanOutlined />} onClick={handleParseUdi}>
              Розпарсити
            </Button>
          </Space.Compact>
        </Form.Item>

        <Space size="middle" style={{ display: 'flex' }}>
          <Form.Item
            label="Номер партії"
            name="batchNumber"
            style={{ flex: 1 }}
          >
            <Input placeholder="Номер партії" />
          </Form.Item>

          <Form.Item
            label="Серійний номер"
            name="serialNumber"
            style={{ flex: 1 }}
          >
            <Input placeholder="Серійний номер" />
          </Form.Item>
        </Space>

        <Space size="middle" style={{ display: 'flex' }}>
          <Form.Item
            label="Термін придатності"
            name="expiryDate"
            style={{ flex: 1 }}
          >
            <DatePicker
              format="DD.MM.YYYY"
              placeholder="Оберіть дату"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            label="Кількість"
            name="quantity"
            rules={[{ required: true, message: 'Вкажіть кількість' }]}
            style={{ flex: 1 }}
          >
            <InputNumber
              min={1}
              placeholder="Кількість"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Space>

        <Form.Item
          label="Локація"
          name="locationCode"
          rules={[{ required: true, message: 'Оберіть локацію' }]}
        >
          <Select
            showSearch
            placeholder="Оберіть локацію"
            options={mdLocationOptions}
            filterOption={(mdInput, mdOption) =>
              (mdOption?.label ?? '').toLowerCase().includes(mdInput.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={mdCreateReceipt.isPending}
            size="large"
          >
            Оприбуткувати
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}
